<template>
  <div>
  <modal
    class="copy-items-modal"
    :title="isConflictStep ? '' : 'Copy Items'"
    :description="isConflictStep ? '' : 'Select which items to copy and choose the destination'"
    :open="open"
    @close="handleModalClose"
  >
    <template v-if="isConflictStep" #header>
      <div class="result-modal-header">
        <button type="button" class="back-btn" @click="goBackToCopyItems">
          <img src="~@/assets/images/icons/arrow-back.svg" alt="Back">
        </button>
        <div class="result-header-text">
          <h3 class="main-title">Copy Results</h3>
          <p class="modal-description">Review conflicts and choose how to handle each</p>
        </div>
      </div>
    </template>
    <div :class="['copy-modal-body', { 'copy-modal-body--results': isConflictStep }]">
      <template v-if="!isConflictStep">
        <div class="copying-from-box">
          <p class="meta-label">Copying from:</p>
          <div class="copying-from-value">
            <img src="~@/assets/images/icons/copy-icon.svg" alt="copy" class="copy-source-icon">
            <span class="source-list-name">{{ sourceItem?.name || sourceListName }}</span>
            <span v-if="sourceItem?.abbreviation" class="abbr-chip">{{ sourceItem.abbreviation }}</span>
          </div>
        </div>

        <div v-if="visibleItems.length" class="section-head">
          <h4>Select Items to Copy</h4>
          <button class="link-btn" type="button" :disabled="itemsLoading" @click="toggleSelection">
            {{ allSelected ? 'Deselect All' : 'Select All' }}
          </button>
        </div>

        <div v-if="visibleItems.length" class="items-box">
          <div v-if="itemsLoading" class="items-loading-state">
            <loading-circle />
          </div>
          <div v-else-if="!visibleItems.length" class="items-empty-state">
            No items to be selected.
          </div>
          <label v-else v-for="item in visibleItems" :key="item.id" class="item-row">
            <input
              type="checkbox"
              :value="item.id"
              v-model="selectedItemIds"
            >
            <span class="item-name">{{ item.name }}</span>
            <span v-if="item.abbreviation" class="abbr-chip">{{ item.abbreviation }}</span>
          </label>
        </div>

        <div v-if="visibleItems.length" class="selection-summary">
          {{ itemsLoading ? 'Loading items...' : `${selectedCount} item(s) of ${visibleItems.length} selected for copying` }}
        </div>

        <div class="destination-section">
          <h4>Destination</h4>

          <label class="field-label">Target List</label>
          <select v-model.number="selectedTargetListId" class="field-select">
            <option
              v-for="list in targetLists"
              :key="list.id"
              :value="list.id"
            >
              {{ list.name }}
            </option>
          </select>

          <label class="field-label">Target Parent (optional)</label>
          <div ref="targetParentPicker" class="target-parent-picker">
            <button
              ref="targetParentButton"
              type="button"
              class="field-select field-select-btn"
              @click="toggleTargetParentDropdown"
            >
              <span class="selected-label">{{ resolvedSelectedParentLabel }}</span>
              <span class="picker-caret" :class="{ open: targetParentOpen }">&#9662;</span>
            </button>
          </div>
        </div>

        <div class="action-row">
          <button type="button" class="btn btn-black text-xs" @click="$emit('close')">
            Cancel
          </button>
          <button
            type="button"
            class="btn text-xs copy-btn"
            :disabled="!canSubmit || loading || previewLoading"
            @click="startCopyFlow"
          >
             Copy{{ selectedCount > 0 ? ` (${selectedCount})` : '' }}
          </button>
        </div>
      </template>

      <template v-else>
        <div class="status-cards">
          <button
            type="button"
            class="status-card success status-card--clickable"
            :class="{ 'status-card--active': isResultSectionExpanded('copied') }"
            :disabled="!copiedResultItems.length"
            :aria-expanded="isResultSectionExpanded('copied')"
            @click="toggleResultSection('copied')"
          >
            <div class="status-count">{{ successCount }} Copied</div>
            <div class="status-sub">Added successfully</div>
          </button>
          <button
            type="button"
            class="status-card skipped status-card--clickable"
            :class="{ 'status-card--active': isResultSectionExpanded('skipped') }"
            :disabled="!skippedResultItems.length"
            :aria-expanded="isResultSectionExpanded('skipped')"
            @click="toggleResultSection('skipped')"
          >
            <div class="status-count">{{ skippedCount }} Skipped</div>
            <div class="status-sub">Skipped as the exact value exists.</div>
          </button>
          <button
            type="button"
            class="status-card revived status-card--clickable"
            :class="{ 'status-card--active': isResultSectionExpanded('revived') }"
            :disabled="!revivedResultItems.length"
            :aria-expanded="isResultSectionExpanded('revived')"
            @click="toggleResultSection('revived')"
          >
            <div class="status-count">{{ revivedCount }} Revived</div>
            <div class="status-sub">Successfully revived values.</div>
          </button>
          <div class="status-card warning">
            <div class="status-count">{{ reviewConflicts.length }} Conflicts</div>
            <div class="status-sub">Need your attention</div>
          </div>
        </div>

        <div class="copy-results-content">

        <div v-if="hasExpandedResultSections" class="expanded-result-groups">
          <div v-if="isResultSectionExpanded('copied') && copiedResultItems.length" class="result-group result-group--copied">
            <div class="result-group__title">Copied items</div>
            <div class="result-item-list">
              <div v-for="(item, index) in copiedResultItems" :key="`copied-${index}-${item}`" class="result-item">
                <div class="result-title-row">
                  <span class="result-title">{{ item }}</span>
                </div>
                <div class="result-msg-line">
                  <span class="result-badge copied">Copied</span>
                  <span class="result-msg-inline">Added successfully</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isResultSectionExpanded('skipped') && skippedResultItems.length" class="result-group result-group--skipped">
            <div class="result-group__title">Skipped items</div>
            <div class="result-item-list">
              <div v-for="item in skippedResultItems" :key="`skipped-${item.key}`" class="result-item result-item--skipped">
                <div class="result-title-row">
                  <span class="result-title">{{ item.name }}</span>
                  <span v-if="item.abbreviation" class="abbr-chip">{{ item.abbreviation }}</span>
                </div>
                <div class="result-msg-line">
                  <span class="result-badge skipped">Skipped</span>
                  <span class="result-msg-inline">Exact match already exists in destination.</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isResultSectionExpanded('revived') && revivedResultItems.length" class="result-group result-group--revived">
            <div class="result-group__title">Revived items</div>
            <div class="result-item-list">
              <div v-for="(item, index) in revivedResultItems" :key="`revived-${index}-${item}`" class="result-item result-item--revived">
                <div class="result-title-row">
                  <span class="result-title">{{ item }}</span>
                </div>
                <div class="result-msg-line">
                  <span class="result-badge revived">Revived</span>
                  <span class="result-msg-inline">Restored from deleted values.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="reviewConflicts.length" class="result-group result-group--conflicts">
          <div class="result-group__title">Conflicts</div>
          <div
            v-for="conflict in reviewConflicts"
            :key="conflict.id"
            :class="['conflict-card', { 'conflict-card--deleted': conflict.deleted }]"
          >
            <div class="conflict-title-row">
              <span class="conflict-title">{{ conflict.name }}</span>
              <span v-if="conflict.abbreviation" class="abbr-chip">{{ conflict.abbreviation }}</span>
            </div>
            <div class="conflict-msg-line">
              <span v-if="conflict.deleted" class="conflict-badge deleted">Deleted copy found</span>
              <span v-else-if="conflict.kind === 'abbreviation_exists'" class="conflict-badge exists">Abbreviation exists</span>
              <span v-else class="conflict-badge exists">Name exists</span>
              <span class="conflict-msg-inline">
                <template v-if="conflict.kind === 'name_exists'">
                  <template v-if="conflict.deleted">
                    Name "{{ conflict.source_name || conflict.name }}" already exists.
                  </template>
                  <template v-else>
                    Name "{{ conflict.source_name || conflict.name }}" already exists.
                  </template>
                </template>
                <template v-else-if="conflict.kind === 'abbreviation_exists'">
                  Abbreviation "{{ conflict.abbreviation }}" already exists in destination
                </template>
                <template v-else>
                  "{{ conflict.name }}" already exists in destination
                </template>
              </span>
            </div>
            <div class="conflict-actions">
              <template v-if="!isEditingConflict(conflict.id)">
                <button
                  type="button"
                  class="resolution-btn skip"
                  :class="{ active: getResolution(conflict.id) === 'skip' }"
                  @click="setResolution(conflict.id, 'skip')"
                >
                  Skip
                </button>
                <button
                  type="button"
                  class="resolution-btn edit"
                  :class="{ active: getResolution(conflict.id) === 'edited' }"
                  @click="openConflictEditor(conflict)"
                >
                  {{ getConflictActionLabel(conflict) }}
                </button>
              </template>
              <template v-else>
                <div class="conflict-inline-editor">
                  <input
                    type="text"
                    class="conflict-edit-input"
                    :value="getConflictDraftValue(conflict.id)"
                    :placeholder="getConflictFieldLabel(conflict)"
                    @input="updateConflictDraftValue(conflict.id, $event.target.value)"
                  >
                  <button type="button" class="btn btn-black conflict-inline-btn" @click="cancelConflictEdit(conflict.id)">
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="btn"
                    :disabled="!canSaveConflictEdit(conflict)"
                    @click="saveConflictEdit(conflict)"
                  >
                    Save
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>
        </div>

        <div class="action-row">
          <button type="button" class="btn btn-black text-xs" @click="handleResultClose">
            Close
          </button>
          <button type="button" class="btn text-xs copy-btn" :disabled="applyingResolutions || resolvedCount === 0" @click="applyResolutions">
            {{ applyingResolutions ? 'Applying...' : `Apply ${resolvedCount}/${reviewConflicts.length} Resolutions` }}
          </button>
        </div>
      </template>
    </div>
  </modal>
    <div
      v-if="targetParentOpen"
      ref="targetParentDropdown"
      class="target-parent-dropdown target-parent-dropdown--portal"
      :class="`target-parent-dropdown--${targetParentPlacement}`"
      :style="targetParentDropdownStyle"
      @mousedown.stop
    >
      <div
        class="tree-row no-parent-row"
        :class="{ selected: selectedTargetParentId == null }"
        @click="selectNoParent"
      >
        No parent (top level)
      </div>

      <div
        v-for="entry in visibleParentNodes"
        :key="entry.id"
        class="tree-row"
        :class="{
          selected: Number(selectedTargetParentId) === entry.id,
          active: activeParentId === entry.id
        }"
      >
        <button
          v-if="entry.hasChildren"
          type="button"
          class="tree-caret-btn"
          :style="{ marginLeft: `${12 + (entry.depth * 20)}px` }"
          @click.stop="toggleParentExpansion(entry)"
        >
          <span class="tree-caret-space">
            <span v-if="entry.isLoading" class="mini-loader"></span>
            <span v-else class="tree-caret" :class="{ open: entry.isExpanded }">&#9662;</span>
          </span>
        </button>
        <span
          v-else
          class="tree-spacer"
          :style="{ marginLeft: `${12 + (entry.depth * 20)}px` }"
        ></span>
        <button
          type="button"
          class="tree-label-btn"
          @click="selectParent(entry)"
        >
          <span class="tree-label">{{ formatNodeLabel(entry.node) }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Modal from '@/components/common/modal.vue'
import loadingCircle from '@/components/common/loadingCircle.vue'
import { GetCustomListChild, CopyCustomListValues, CreateCustomListValues } from '@/api'

export default {
  name: 'CopyListValuesModal',
  components: { Modal, loadingCircle },
  props: {
    open: { type: Boolean, default: false },
    sourceListName: { type: String, default: '' },
    sourceItem: { type: Object, default: () => ({}) },
    items: { type: Array, default: () => [] },
    targetLists: { type: Array, default: () => [] },
    targetParentOptions: { type: Array, default: () => [] },
    defaultTargetListId: { type: [Number, String], default: null },
    loading: { type: Boolean, default: false },
    itemsLoading: { type: Boolean, default: false }
  },
  data () {
    return {
      selectedItemIds: [],
      selectedTargetListId: null,
      selectedTargetParentId: null,
      targetParentOpen: false,
      expandedParents: {},
      checkedParents: {},
      loadingParents: {},
      childrenCache: {},
      activeParentId: null,
      targetParentPlacement: 'bottom',
      targetParentDropdownStyle: {},
      isConflictStep: false,
      conflicts: [],
      pendingItemsById: {},
      pendingValueIds: [],
      copiedCountFromApi: 0,
      skippedDuplicateCount: 0,
      revivedCountFromApi: 0,
      copiedResultItems: [],
      skippedResultItems: [],
      revivedResultItems: [],
      activeResultSection: null,
      previewLoading: false,
      conflictResolutions: {},
      conflictDraftValues: {},
      editingConflictIds: {},
      conflictEditedValues: {},
      applyingResolutions: false
    }
  },
  computed: {
    visibleItems () {
      return (Array.isArray(this.items) ? this.items : []).filter(item => !item?.deleted)
    },
    selectedCount () {
      return this.selectedItemIds.length
    },
    allSelected () {
      return this.visibleItems.length > 0 && this.selectedCount === this.visibleItems.length
    },
    canSubmit () {
      return Boolean(this.selectedTargetListId) && !this.itemsLoading
    },
    targetParentTree () {
      return Array.isArray(this.targetParentOptions) ? this.targetParentOptions : []
    },
    visibleTargetParentTree () {
      return this.targetParentTree.filter(node => !node?.deleted)
    },
    resolvedSelectedParentLabel () {
      if (this.selectedTargetParentId == null) return 'No parent (top level)'
      const stack = [...this.visibleTargetParentTree]
      while (stack.length) {
        const node = stack.pop()
        if (Number(node.id) === Number(this.selectedTargetParentId)) return this.formatNodeLabel(node)
        const children = this.getChildren(node)
        if (children.length) stack.push(...children)
      }
      return 'No parent (top level)'
    },
    visibleParentNodes () {
      const nodes = []
      const traverse = (list = [], depth = 0) => {
        list.forEach(node => {
          if (node?.deleted) return
          const nodeId = Number(node.id)
          nodes.push({
            node,
            depth,
            id: nodeId,
            hasChildren: this.hasChildren(node),
            isExpanded: Boolean(this.expandedParents[nodeId]),
            isLoading: Boolean(this.loadingParents[nodeId])
          })
          if (this.expandedParents[nodeId]) {
            const children = this.getChildren(node).filter(child => !child?.deleted)
            if (children.length) traverse(children, depth + 1)
          }
        })
      }
      traverse(this.visibleTargetParentTree, 0)
      return nodes
    },
    successCount () {
      return Math.max(Number(this.copiedCountFromApi) || 0, 0)
    },
    skippedCount () {
      return Math.max(Number(this.skippedResultItems.length) || 0, 0)
    },
    revivedCount () {
      return Math.max(Number(this.revivedResultItems.length) || 0, 0)
    },
    reviewConflicts () {
      return (Array.isArray(this.conflicts) ? this.conflicts : []).filter(conflict => conflict.kind !== 'skipped_exact_match')
    },
    resolvedCount () {
      return this.reviewConflicts.filter(conflict => Boolean(this.conflictResolutions[conflict.id])).length
    },
    hasExpandedResultSections () {
      return Boolean(this.activeResultSection)
    }
  },
  watch: {
    open: {
      immediate: true,
      handler (value) {
        if (!value) return
        this.selectedItemIds = this.visibleItems.map(item => Number(item.id))
        this.selectedTargetListId = this.defaultTargetListId ? Number(this.defaultTargetListId) : (this.targetLists[0]?.id || null)
        this.isConflictStep = false
        this.conflicts = []
        this.pendingItemsById = {}
        this.pendingValueIds = []
        this.copiedCountFromApi = 0
        this.skippedDuplicateCount = 0
        this.revivedCountFromApi = 0
        this.copiedResultItems = []
        this.skippedResultItems = []
        this.revivedResultItems = []
        this.activeResultSection = null
        this.previewLoading = false
        this.conflictResolutions = {}
        this.conflictDraftValues = {}
        this.editingConflictIds = {}
        this.conflictEditedValues = {}
        this.applyingResolutions = false
        this.resetTargetParentState()
        this.emitTargetListChange()
      }
    },
    items: {
      deep: true,
      handler () {
        if (!this.open) return
        this.selectedItemIds = this.visibleItems.map(item => Number(item.id))
      }
    },
    selectedTargetListId () {
      this.resetTargetParentState()
      this.emitTargetListChange()
    },
    targetParentOptions () {
      this.expandedParents = {}
      this.checkedParents = {}
      this.loadingParents = {}
      this.childrenCache = {}
    }
  },
  methods: {
    resetTargetParentState () {
      this.selectedTargetParentId = null
      this.targetParentOpen = false
      this.activeParentId = null
      this.targetParentPlacement = 'bottom'
      this.targetParentDropdownStyle = {}
      this.expandedParents = {}
      this.checkedParents = {}
      this.loadingParents = {}
      this.childrenCache = {}
    },
    toggleSelection () {
      this.selectedItemIds = this.allSelected ? [] : this.visibleItems.map(item => Number(item.id))
    },
    emitTargetListChange () {
      if (!this.selectedTargetListId) return
      this.$emit('target-list-change', Number(this.selectedTargetListId))
    },
    getChildren (node) {
      const nodeId = Number(node.id)
      if (Array.isArray(this.childrenCache[nodeId])) return this.childrenCache[nodeId]
      if (Array.isArray(node.children)) return node.children
      return []
    },
    hasChildren (node) {
      const aggregateCount = node?.child_values_aggregate?.aggregate?.count || 0
      return aggregateCount > 0 || this.getChildren(node).length > 0
    },
    formatNodeLabel (node) {
      const abbreviation = node?.abbreviation ? ` (${node.abbreviation})` : ''
      return `${node?.name || ''}${abbreviation}`
    },
    toggleTargetParentDropdown () {
      this.targetParentOpen = !this.targetParentOpen
      if (this.targetParentOpen) {
        this.$nextTick(() => {
          this.updateTargetParentDropdownPosition()
        })
      }
    },
    async ensureChildrenLoaded (node) {
      const nodeId = Number(node.id)
      if (this.checkedParents[nodeId]) return this.getChildren(node)
      this.$set(this.loadingParents, nodeId, true)
      try {
        const res = await GetCustomListChild(nodeId)
        const children = res?.custom_list_values || []
        this.$set(this.childrenCache, nodeId, children)
        this.$set(this.checkedParents, nodeId, true)
        return children
      } catch (err) {
        this.$notify.alert(err?.message || 'Unable to load target parent children')
        this.$set(this.childrenCache, nodeId, [])
        this.$set(this.checkedParents, nodeId, true)
        return []
      } finally {
        this.$delete(this.loadingParents, nodeId)
      }
    },
    updateTargetParentDropdownPosition () {
      const button = this.$refs.targetParentButton
      if (!button) return
      const rect = button.getBoundingClientRect()
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight
      const gap = 6
      const viewportPadding = 12
      const maxDropdownHeight = 250
      const minDropdownHeight = 120
      const availableBelow = Math.max(viewportPadding, viewportHeight - rect.bottom - gap - viewportPadding)
      const availableAbove = Math.max(viewportPadding, rect.top - gap - viewportPadding)
      const openUpward = availableBelow < minDropdownHeight && availableAbove > availableBelow
      const constrainedHeight = Math.min(
        maxDropdownHeight,
        Math.max(minDropdownHeight, openUpward ? availableAbove : availableBelow)
      )

      this.targetParentPlacement = openUpward ? 'top' : 'bottom'
      this.targetParentDropdownStyle = {
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        maxHeight: `${constrainedHeight}px`,
        overflowY: 'auto',
        top: openUpward ? 'auto' : `${Math.max(viewportPadding, rect.bottom + gap)}px`,
        bottom: openUpward ? `${Math.max(viewportPadding, viewportHeight - rect.top + gap)}px` : 'auto'
      }
    },
    handleTargetParentOutsideClick (event) {
      if (!this.targetParentOpen) return
      const picker = this.$refs.targetParentPicker
      const dropdown = this.$refs.targetParentDropdown
      const target = event.target
      if (
        (picker && picker.contains(target)) ||
        (dropdown && dropdown.contains(target))
      ) {
        return
      }
      this.targetParentOpen = false
    },
    async toggleParentExpansion (entry) {
      this.activeParentId = entry.id
      const children = await this.ensureChildrenLoaded(entry.node)
      if (!children.length) return
      if (this.expandedParents[entry.id]) this.$delete(this.expandedParents, entry.id)
      else this.$set(this.expandedParents, entry.id, true)
      this.$nextTick(() => {
        this.updateTargetParentDropdownPosition()
      })
    },
    selectParent (entry) {
      this.selectedTargetParentId = Number(entry.id)
      this.targetParentOpen = false
    },
    selectNoParent () {
      this.selectedTargetParentId = null
      this.targetParentOpen = false
      this.activeParentId = null
    },
    goBackToCopyItems () {
      this.isConflictStep = false
    },
    getConflictEditField (conflict) {
      return conflict?.kind === 'abbreviation_exists' ? 'abbreviation' : 'name'
    },
    getConflictFieldLabel (conflict) {
      return this.getConflictEditField(conflict) === 'abbreviation'
        ? 'Change the Abbreviation'
        : 'Change the name'
    },
    normalizeText (value) {
      return (value || '').trim().toLowerCase()
    },
    normalizeResultEntries (entries = []) {
      return (Array.isArray(entries) ? entries : []).map(entry => (
        typeof entry === 'string'
          ? entry
          : (entry?.name || entry?.source_name || '')
      )).filter(Boolean)
    },
    isResultSectionExpanded (section) {
      return this.activeResultSection === section
    },
    toggleResultSection (section) {
      if (!['copied', 'skipped', 'revived'].includes(section)) return
      if (!this[`${section}ResultItems`].length) return
      this.activeResultSection = this.activeResultSection === section ? null : section
    },
    isExactSourceMatchConflict (entry, itemMap = {}) {
      const entryId = Number(entry?.id) || entry?.id
      const sourceItem = itemMap?.[entryId] || {}
      const sourceName = this.normalizeText(entry?.source_name || sourceItem?.name)
      const sourceAbbreviation = this.normalizeText(entry?.source_abbreviation || sourceItem?.abbreviation)
      const destinationName = this.normalizeText(entry?.name)
      const destinationAbbreviation = this.normalizeText(entry?.abbreviation)

      return Boolean(sourceName && destinationName) &&
        sourceName === destinationName &&
        sourceAbbreviation === destinationAbbreviation
    },
    getConflictActionLabel (conflict) {
      return this.getConflictFieldLabel(conflict)
    },
    isEditingConflict (conflictId) {
      return Boolean(this.editingConflictIds[conflictId])
    },
    getConflictSourceValue (conflict) {
      const field = this.getConflictEditField(conflict)
      if (field === 'abbreviation') {
        return conflict.source_abbreviation || conflict.abbreviation || ''
      }
      return conflict.source_name || conflict.name || ''
    },
    getResolvedConflictLabel (conflict) {
      const field = this.getConflictEditField(conflict)
      const editedValue = (this.conflictEditedValues[conflict.id] || '').trim()
      if (editedValue) return editedValue
      return field === 'abbreviation'
        ? (conflict.source_abbreviation || conflict.abbreviation || '')
        : (conflict.source_name || conflict.name || '')
    },
    getConflictDraftValue (conflictId) {
      return this.conflictDraftValues[conflictId] || ''
    },
    updateConflictDraftValue (conflictId, value) {
      this.$set(this.conflictDraftValues, conflictId, value)
    },
    openConflictEditor (conflict) {
      const conflictId = conflict.id
      const savedValue = this.conflictEditedValues[conflictId]
      const hasDraftValue = Object.prototype.hasOwnProperty.call(this.conflictDraftValues, conflictId)
      const draftValue = savedValue || this.getConflictSourceValue(conflict)

      Object.keys(this.editingConflictIds).forEach(activeConflictId => {
        if (String(activeConflictId) !== String(conflictId)) {
          this.$delete(this.editingConflictIds, activeConflictId)
        }
      })

      if (!hasDraftValue) {
        this.$set(this.conflictDraftValues, conflictId, draftValue)
      }
      this.$set(this.editingConflictIds, conflictId, true)
    },
    cancelConflictEdit (conflictId) {
      this.$delete(this.conflictDraftValues, conflictId)
      this.$delete(this.editingConflictIds, conflictId)
    },
    saveConflictEdit (conflict) {
      const conflictId = conflict.id
      const nextValue = (this.getConflictDraftValue(conflictId) || '').trim()
      const originalValue = (this.getConflictSourceValue(conflict) || '').trim()
      if (!nextValue) {
        this.$notify.alert('Please enter a value before saving')
        return
      }
      if (nextValue.toLowerCase() === originalValue.toLowerCase()) {
        this.$notify.alert('Please enter a different value before saving')
        return
      }
      this.$set(this.conflictEditedValues, conflictId, nextValue)
      this.setResolution(conflictId, 'edited')
      this.$delete(this.conflictDraftValues, conflictId)
      this.$delete(this.editingConflictIds, conflictId)
    },
    canSaveConflictEdit (conflict) {
      const conflictId = conflict.id
      const nextValue = (this.getConflictDraftValue(conflictId) || '').trim()
      const originalValue = (this.getConflictSourceValue(conflict) || '').trim()
      return Boolean(nextValue) && nextValue.toLowerCase() !== originalValue.toLowerCase()
    },
    getResolution (conflictId) {
      return this.conflictResolutions[conflictId] || null
    },
    setResolution (conflictId, resolution) {
      this.$set(this.conflictResolutions, conflictId, resolution)
    },
    buildCopyName (name) {
      return `${name} (Copy)`
    },
    buildCopyNameWithAbbreviation (name, abbreviation) {
      if (!abbreviation) return this.buildCopyName(name)
      return `${name} (${abbreviation} (Copy))`
    },
    buildDeletedNameConflictCopyName (name, abbreviation) {
      if (!abbreviation) return this.buildCopyName(name)
      return `${name} (Copy) (${abbreviation})`
    },
    buildCopyAbbreviation (abbreviation) {
      if (!abbreviation) return null
      return `${abbreviation} (Copy)`
    },
    resolveConflictName (conflict) {
      const field = this.getConflictEditField(conflict)
      if (field === 'name') {
        const editedValue = (this.conflictEditedValues[conflict.id] || '').trim()
        return editedValue || (conflict?.source_name || conflict?.name || '') || null
      }
      return (conflict?.source_name || conflict?.name || '') || null
    },
    resolveConflictAbbreviationValue (conflict) {
      const field = this.getConflictEditField(conflict)
      if (field === 'abbreviation') {
        const editedValue = (this.conflictEditedValues[conflict.id] || '').trim()
        return editedValue || (conflict?.source_abbreviation || this.resolveConflictAbbreviation(conflict)) || null
      }
      return conflict?.source_abbreviation || this.resolveConflictAbbreviation(conflict) || null
    },
    resolveConflictAbbreviation (conflict) {
      return conflict?.abbreviation || conflict?.abbreviated_name || conflict?.abbreviatedName || null
    },
    normalizeAlreadyExistsConflicts (list = [], itemMap = {}) {
      return (Array.isArray(list) ? list : []).reduce((result, entry) => {
        const entryId = Number(entry?.id) || entry?.id
        const sourceItem = itemMap?.[entryId] || {}
        const sourceName = (entry?.source_name || sourceItem?.name || '').trim()
        const sourceAbbreviation = (entry?.source_abbreviation || sourceItem?.abbreviation || '').trim()
        const destinationName = (entry?.name || '').trim()
        const destinationAbbreviation = (entry?.abbreviation || '').trim()
        const nameMatches = this.normalizeText(sourceName) === this.normalizeText(destinationName)
        const abbreviationMatches = this.normalizeText(sourceAbbreviation) === this.normalizeText(destinationAbbreviation)
        let kind = 'existing'
        if (this.isExactSourceMatchConflict(entry, itemMap)) kind = 'skipped_exact_match'
        else if (nameMatches && !abbreviationMatches) kind = 'name_exists'
        else if (!nameMatches && abbreviationMatches) kind = 'abbreviation_exists'
        result.push({
          id: entryId,
          name: destinationName || sourceName || sourceItem?.name || null,
          abbreviation: destinationAbbreviation || sourceAbbreviation || sourceItem?.abbreviation || null,
          parent_id: entry?.parent_id != null ? Number(entry.parent_id) : null,
          source_name: sourceName || null,
          source_abbreviation: sourceAbbreviation || null,
          deleted: Boolean(entry?.deleted),
          kind
        })
        return result
      }, [])
    },
    async applyResolutions () {
      if (this.applyingResolutions) return
      this.applyingResolutions = true
      try {
        const copyConflicts = this.reviewConflicts
          .filter(conflict => {
            const resolution = this.getResolution(conflict.id)
            return resolution && resolution !== 'skip'
          })

        const createPayload = copyConflicts.map(conflict => ({
          custom_list_id: Number(this.selectedTargetListId),
          parent_id: conflict.parent_id != null
            ? Number(conflict.parent_id)
            : (this.selectedTargetParentId != null ? Number(this.selectedTargetParentId) : null),
          name: this.resolveConflictName(conflict),
          abbreviation: this.resolveConflictAbbreviationValue(conflict)
        }))

        if (createPayload.length) {
          await CreateCustomListValues(createPayload)
        }

        this.$notify.success('Values copied successfully')
        this.$emit('applied-resolutions', {
          targetListId: Number(this.selectedTargetListId),
          createdCount: createPayload.length + this.successCount,
          revivedCount: this.revivedCount
        })
        this.$emit('close')
      } catch (err) {
        this.$notify.alert(err?.message || 'Unable to apply resolutions')
      } finally {
        this.applyingResolutions = false
      }
    },
    completeCopiedResults () {
      this.$notify.success('Values copied successfully')
      this.$emit('applied-resolutions', {
        targetListId: Number(this.selectedTargetListId),
        createdCount: this.successCount,
        revivedCount: this.revivedCount
      })
      this.$emit('close')
    },
    handleResultClose () {
      if (this.reviewConflicts.length) {
        this.$emit('close')
        return
      }
      this.completeCopiedResults()
    },
    handleModalClose () {
      if (this.isConflictStep && !this.reviewConflicts.length) {
        this.completeCopiedResults()
        return
      }
      this.$emit('close')
    },
    buildPendingItems () {
      const sourceId = Number(this.sourceItem?.id)
      const selectedIds = this.selectedItemIds.map(id => Number(id)).filter(id => Number.isInteger(id))
      const valueIds = Array.from(new Set([
        ...selectedIds,
        ...(Number.isInteger(sourceId) ? [sourceId] : [])
      ]))

      const itemMap = {}
      const source = this.sourceItem?.id ? this.sourceItem : null
      if (source) itemMap[Number(source.id)] = source
      this.visibleItems.forEach(item => { itemMap[Number(item.id)] = item })
      return { valueIds, itemMap }
    },
    async startCopyFlow () {
      if (!this.canSubmit || this.loading || this.previewLoading) return
      const { valueIds, itemMap } = this.buildPendingItems()
      if (!valueIds.length) {
        this.$notify.alert('Please select at least one item to copy')
        return
      }
      this.previewLoading = true
      try {
        const res = await CopyCustomListValues(
          Number(this.selectedTargetListId),
          this.selectedTargetParentId != null ? Number(this.selectedTargetParentId) : null,
          valueIds
        )
        const payload = res?.copy_custom_list_values || {}
        const alreadyExistsItems = Array.isArray(payload?.already_exists) ? payload.already_exists : []
        const skippedDuplicateCount = alreadyExistsItems.filter(entry => this.isExactSourceMatchConflict(entry, itemMap)).length
        const existingItems = this.normalizeAlreadyExistsConflicts(alreadyExistsItems, itemMap)
        this.pendingValueIds = valueIds
        this.pendingItemsById = itemMap
        const skippedItems = existingItems.filter(conflict => conflict.kind === 'skipped_exact_match')
        const revivedItems = this.normalizeResultEntries(payload?.revived || [])
        const copiedItems = this.normalizeResultEntries(payload?.new || [])
        this.conflicts = existingItems
        this.conflictResolutions = existingItems.reduce((resolutions, conflict) => {
          if (conflict.kind !== 'skipped_exact_match') {
            return resolutions
          }
          resolutions[conflict.id] = 'skip'
          return resolutions
        }, {})
        this.conflictDraftValues = {}
        this.editingConflictIds = {}
        this.conflictEditedValues = {}
        this.copiedCountFromApi = Array.isArray(payload?.new) ? payload.new.length : 0
        this.skippedDuplicateCount = skippedDuplicateCount
        this.copiedResultItems = copiedItems
        this.skippedResultItems = skippedItems
        this.revivedResultItems = revivedItems
        this.revivedCountFromApi = revivedItems.length
        this.activeResultSection = copiedItems.length ? 'copied' : (skippedItems.length ? 'skipped' : (revivedItems.length ? 'revived' : null))
        if (!existingItems.length && !revivedItems.length) {
          this.$notify.success(this.successCount === 1 ? 'Value copied successfully' : 'Values copied successfully')
          this.$emit('applied-resolutions', {
            targetListId: Number(this.selectedTargetListId),
            createdCount: this.successCount,
            revivedCount: this.revivedCount
          })
          this.$emit('close')
          return
        }
        this.isConflictStep = true
      } catch (err) {
        this.$notify.alert(err?.message || 'Something went wrong')
      } finally {
        this.previewLoading = false
      }
    }
  },
  mounted () {
    document.addEventListener('mousedown', this.handleTargetParentOutsideClick)
    window.addEventListener('resize', this.updateTargetParentDropdownPosition)
    window.addEventListener('scroll', this.updateTargetParentDropdownPosition, true)
  },
  beforeDestroy () {
    document.removeEventListener('mousedown', this.handleTargetParentOutsideClick)
    window.removeEventListener('resize', this.updateTargetParentDropdownPosition)
    window.removeEventListener('scroll', this.updateTargetParentDropdownPosition, true)
  }
}
</script>

<style lang="scss" scoped>
.copy-modal-body { width: 430px; max-width: 100%; }
.copy-modal-body--results {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 220px);
}
.copy-results-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
}
.copying-from-box { border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px 12px; margin-bottom: 14px; background: #fbfdff; }
.meta-label { color: #727a84; font-size: var(--text-xs); margin-bottom: 6px; }
.copying-from-value { display: flex; align-items: center; gap: 8px; }
.copy-source-icon { width: 14px; height: 14px; opacity: 0.8; }
.source-list-name { font-size: var(--text-xs2); color: #202124; font-weight: 500; }
.abbr-chip { font-size: var(--text-xs1); color: #5c6773; background: #edf2f7; border: 1px solid #d9e1ea; border-radius: 4px; padding: 1px 6px; }
.section-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; h4 { font-size: var(--text-s); font-weight: 600; margin: 0; } }
.link-btn { border: 0; background: transparent; color: #d8911f; font-size: var(--text-s); cursor: pointer; &:disabled { opacity: 0.5; cursor: not-allowed; } }
.items-box { border: 1px solid #e2e8f0; border-radius: 6px; max-height: 190px; overflow: auto; margin-bottom: 10px; }
.item-row { display: flex; align-items: center; gap: 8px; padding: 10px 12px; border-bottom: 1px solid #f1f5f9; &:last-child { border-bottom: 0; } }
.items-loading-state, .items-empty-state { min-height: 100px; display: flex; align-items: center; justify-content: center; }
.items-empty-state { padding: 10px 12px; color: #6b7280; font-size: var(--text-s); text-align: center; }
.item-name { font-size: var(--text-s); color: #202124; }
.selection-summary { background: #eef3fb; color: #516273; border-radius: 4px; padding: 8px 10px; font-size: var(--text-s); margin-bottom: 16px; }
.destination-section h4 { margin: 0 0 10px; font-size: var(--text-s); font-weight: 600; }
.field-label { display: block; margin-bottom: 4px; font-size: var(--text-s); color: #515963; }
.field-select { width: 100%; height: 32px; border: 1px solid #dbe2ea; border-radius: 4px; background: #f8fafc; padding: 0 10px; margin-bottom: 10px; color: #333; }
.field-select-btn { display: flex; align-items: center; justify-content: space-between; cursor: pointer; text-align: left; }
.selected-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.picker-caret { margin-left: 8px; color: #8b95a1; transform: rotate(0deg); transition: transform 0.15s; &.open { transform: rotate(180deg); } }
.target-parent-picker { position: relative; margin-bottom: 10px; }
.target-parent-dropdown { border: 1px solid #dbe2ea; border-radius: 4px; background: #fff; max-height: 260px; overflow: auto; box-shadow: 0 7px 22px rgba(10, 20, 40, 0.12); }
.target-parent-dropdown--portal { position: fixed; z-index: 1000; }
.tree-row { display: flex; align-items: center; min-height: 32px; font-size: var(--text-s); color: #2f3741; cursor: pointer; &:hover { background: #f7f9fc; } &.active { background: #f8ecd0; } &.selected { background: #f4e0a3; color: #5f4a12; } }
.no-parent-row { border-bottom: 1px solid #eef2f7; font-style: italic; color: #6b7280; }
.no-parent-row,
.tree-label-btn { padding: 6px 10px; }
.tree-label-btn { flex: 1 1 auto; min-width: 0; border: 0; background: transparent; text-align: left; color: inherit; cursor: pointer; }
.tree-caret-btn { border: 0; background: transparent; padding: 0; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; flex: 0 0 auto; }
.tree-caret-space { width: 14px; height: 14px; margin-right: 8px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.tree-spacer { width: 14px; margin-right: 8px; flex: 0 0 auto; }
.tree-caret { font-size: 11px; color: #8b95a1; transform: rotate(-90deg); transition: transform 0.15s; &.open { transform: rotate(0deg); } }
.tree-label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mini-loader { width: 10px; height: 10px; border: 2px solid #d5dde8; border-top-color: #9ba7b6; border-radius: 50%; animation: spin 0.7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.status-cards { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-bottom: 12px; }
.status-card {
  appearance: none;
  width: 100%;
  text-align: left;
}
.status-card:disabled {
  opacity: 0.95;
  cursor: default;
}
.result-modal-header { width: 100%; display: flex; flex-direction: column; align-items: flex-start; gap: 6px; }
.result-header-text { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; }
.result-modal-header .main-title { margin: 0; font-size: var(--text-base); color: #111827; font-weight: 600; }
.result-modal-header .modal-description { margin: 0; font-size: var(--text-xs1); color: #b7791f; }
.back-btn { border: 0; background: transparent; cursor: pointer; padding: 0; margin: 0; }
.back-btn img { width: 14px; height: 14px; }
.status-card { flex: 1; border-radius: 6px; padding: 10px; border: 1px solid; }
.status-card.success { border-color: #9ad9b0; background: #eef9f2; color: #22864b; }
.status-card.skipped { border-color: #9ec5fe; background: #eef6ff; color: #1d4ed8; }
.status-card.revived { border-color: #7fd8d1; background: #eefcfb; color: #0f766e; }
.status-card.warning { border-color: #efd08e; background: #fff8e8; color: #a86400; }
.status-card--clickable {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}
.status-card--clickable:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(17, 24, 39, 0.08);
}
.status-card--active {
  box-shadow: 0 0 0 2px rgba(76, 91, 255, 0.12);
}
.status-count { font-size: var(--text-s); font-weight: 600; }
.status-sub { font-size: var(--text-xs1); }
.expanded-result-groups { margin-bottom: 12px; }
.result-group {
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid #e5e7eb;
  background: #fbfcfe;
}
.result-group--conflicts {
  padding: 0;
  border: 0;
  background: transparent;
}
.result-group__title {
  font-size: var(--text-s);
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
}
.result-item-list { display: flex; flex-direction: column; gap: 8px; }
.result-item {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  padding: 10px;
}
.result-item--skipped {
  border-color: #efd08e;
  background: #fffef4;
}
.result-item--revived {
  border-color: #7fd8d1;
  background: #f0fdfa;
}
.result-title-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.result-title { font-size: var(--text-s); font-weight: 600; color: #1f2937; }
.result-msg-line { display: flex; align-items: center; gap: 6px; }
.result-badge { display: inline-block; font-size: var(--text-xs1); padding: 1px 6px; border-radius: 4px; }
.result-badge.copied { background: #e6f7ec; color: #22864b; }
.result-badge.skipped { background: #fff0b8; color: #8a5a00; }
.result-badge.revived { background: #ccfbf1; color: #0f766e; }
.result-msg-inline { font-size: var(--text-xs1); color: #6b7280; }
.result-item--skipped .result-msg-inline { color: #8a5a00; }
.result-item--revived .result-msg-inline { color: #0f766e; }
.conflict-card { border: 1px solid #efd08e; border-radius: 6px; padding: 10px; margin-bottom: 10px; background: #fffdf8; }
.conflict-card.conflict-card--deleted { border-color: #d9c2ff; background: #f8f3ff; }
.conflict-title-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.conflict-title { font-size: var(--text-s); font-weight: 600; color: #1f2937; }
.conflict-msg-line { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; }
.conflict-msg-inline { font-size: var(--text-xs1); color: #7c5a1f; }
.conflict-card.conflict-card--deleted .conflict-msg-inline { color: #5b3a9a; }
.conflict-badge { display: inline-block; font-size: var(--text-xs1); padding: 1px 6px; border-radius: 4px; margin-bottom: 0; }
.conflict-badge.exists { background: #ffe7e7; color: #9f1239; }
.conflict-badge.deleted { background: #efe4ff; color: #6d28d9; }
.conflict-badge.skipped { background: #fff0b8; color: #8a5a00; }
.conflict-actions { display: flex; gap: 8px; }
.conflict-inline-editor { display: flex; align-items: center; gap: 8px; width: 100%; }
.conflict-inline-editor .conflict-edit-input { flex: 1 1 auto; min-width: 0; }
.conflict-inline-btn { flex: 0 0 auto; white-space: nowrap; }
.conflict-inline-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.resolution-btn { border: 1px solid #d0d7de; background: #f8f9fb; color: #334155; border-radius: 14px; padding: 4px 10px; font-size: var(--text-xs1); cursor: pointer; }
.resolution-btn.skip.active { border-color: #d2a248; background: #fff1c2; color: #8a5a00; }
.resolution-btn.edit.active { border-color: var(--brand-color); background: var(--brand-color); color: #ffffff; }
.resolution-btn.revive.active { border-color: #7e22ce; background: #8b2de2; color: #ffffff; }
.conflict-edit-input { width: 100%; height: 32px; border: 1px solid #d8e0e9; border-radius: 4px; background: #fff; padding: 0 10px; font-size: var(--text-xs); color: #202124; }
.conflict-edit-input:focus { outline: none; border-color: #d2a248; box-shadow: 0 0 0 2px rgba(241, 180, 63, 0.18); }
.conflict-save-btn { border-color: #d2a248; background: #f1b43f; color: #ffffff; }
.conflict-foot { font-size: var(--text-s); color: #4b5563; margin: 8px 0 14px; }
.action-row { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
.copy-btn:disabled { opacity: 0.6; cursor: not-allowed; }
::v-deep .copy-items-modal .modal-header { align-items: flex-start; }
::v-deep .copy-items-modal .modal-header h3 { padding-right: 0 !important; font-weight: 600 !important; }
::v-deep .copy-items-modal .modal-header .modal-close { top: 10px; }
::v-deep .modal.copy-items-modal .modal-body,
::v-deep .modal.copy-items-modal .modal-inner,
::v-deep .modal.copy-items-modal .modal-content { overflow: visible !important; }
::v-deep .modal.copy-items-modal .modal-content { max-height: none !important; }
</style>
